import React, { useState } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { resolveIncompleteItem, IncompleteItem } from '../../../store/sprintSlice';

interface Props {
  sprintId: string;
  items: IncompleteItem[];
  onClose: () => void;
}

type Action = 'backlog' | 'next_sprint' | 'extend';

interface Decision {
  action: Action;
  extendedDate?: string;
}

const typeBadge: Record<IncompleteItem['type'], { label: string; color: string }> = {
  task: { label: 'Task', color: 'bg-blue-100 text-blue-700' },
  feature: { label: 'Feature', color: 'bg-purple-100 text-purple-700' },
  bug: { label: 'Bug', color: 'bg-red-100 text-red-700' },
};

const IncompleteItemsModal: React.FC<Props> = ({ sprintId, items, onClose }) => {
  const dispatch = useAppDispatch();
  const [decisions, setDecisions] = useState<Record<string, Decision>>(() => {
    const init: Record<string, Decision> = {};
    items.forEach((item) => {
      init[item._id] = { action: 'backlog' };
    });
    return init;
  });
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState(0);

  const setAction = (itemId: string, action: Action) => {
    setDecisions((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], action, extendedDate: action === 'extend' ? prev[itemId]?.extendedDate : undefined },
    }));
  };

  const setExtendedDate = (itemId: string, date: string) => {
    setDecisions((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], extendedDate: date },
    }));
  };

  const handleResolveAll = async () => {
    setResolving(true);
    setResolved(0);
    let count = 0;
    for (const item of items) {
      const decision = decisions[item._id];
      await dispatch(
        resolveIncompleteItem({
          sprintId,
          itemId: item._id,
          itemType: item.type,
          action: decision.action,
          newDeadline: decision.action === 'extend' ? decision.extendedDate : undefined,
        })
      );
      count += 1;
      setResolved(count);
    }
    setResolving(false);
    onClose();
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="!mt-0 fixed inset-0 bg-black/40 flex items-center justify-center" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <h2 className="text-lg font-bold text-gray-900 mb-1">
          Resolve Incomplete Items
          <span className="ml-2 text-sm font-normal text-gray-500">({items.length} items)</span>
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          Choose what to do with each incomplete item before closing the sprint.
        </p>

        {/* Items */}
        <div className="space-y-4">
          {items.map((item) => {
            const badge = typeBadge[item.type];
            const decision = decisions[item._id];
            return (
              <div key={item._id} className="border border-gray-200 rounded-lg p-4">
                {/* Item header */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.color}`}>
                    {badge.label}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{item.title}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {item.status}
                  </span>
                  {item.priority && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {item.priority}
                    </span>
                  )}
                  {item.dueDate && (
                    <span className="text-xs text-gray-400 ml-auto">
                      Due {formatDate(item.dueDate)}
                    </span>
                  )}
                </div>

                {/* Radio options */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name={`action-${item._id}`}
                      value="backlog"
                      checked={decision.action === 'backlog'}
                      onChange={() => setAction(item._id, 'backlog')}
                      className="accent-blue-600"
                    />
                    Move to backlog
                  </label>

                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name={`action-${item._id}`}
                      value="next_sprint"
                      checked={decision.action === 'next_sprint'}
                      onChange={() => setAction(item._id, 'next_sprint')}
                      className="accent-blue-600 mt-0.5"
                    />
                    <span>
                      Move to next sprint
                      {decision.action === 'next_sprint' && (
                        <span className="block text-xs text-gray-400 mt-0.5">
                          Will be assigned during next sprint planning
                        </span>
                      )}
                    </span>
                  </label>

                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name={`action-${item._id}`}
                      value="extend"
                      checked={decision.action === 'extend'}
                      onChange={() => setAction(item._id, 'extend')}
                      className="accent-blue-600 mt-0.5"
                    />
                    <span className="flex-1">
                      Extend deadline
                      {decision.action === 'extend' && (
                        <input
                          type="date"
                          value={decision.extendedDate || ''}
                          onChange={(e) => setExtendedDate(item._id, e.target.value)}
                          className="block mt-1 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      )}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={resolving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleResolveAll}
            disabled={resolving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {resolving ? `Resolving ${resolved}/${items.length}...` : 'Resolve All'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncompleteItemsModal;
